import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Droplets,
  TestTube,
  Calendar,
  FileText,
  Building2,
  Send,
  FileUp,
  Camera,
} from "lucide-react";

const SectionCWaterSupply = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const surveyId = localStorage.getItem("survey_id") || "defaultSurvey";

  const [form, setForm] = useState({
    c1: "",
    c2: "",
    c2Description: "",
    c2Photo: null,
    c2Remarks: "",
    c3: "",
    c3Date: "",
    c3Report: null,
    c3Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);
  const [previewImages, setPreviewImages] = useState({
    c2Photo: null,
    c3Report: null,
  });

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_c");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          setForm(savedData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        const isPartial = determinePartialStatus();
        const dataToSave = {
          ...form,
          isPartial: isPartial,
          lastSaved: new Date().toISOString(),
        };
        localStorage.setItem("survey_section_c", JSON.stringify(dataToSave));
        setSaveStatus(t.global.alert_saved_locally);
        setTimeout(() => setSaveStatus(""), 2000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [form, t]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type,
        }));
        return;
      }

      // Store file in form
      setForm((prev) => ({ ...prev, [name]: file }));

      if (file && file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }

      // Clear file error
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user changes input
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleCameraCapture = (file) => {
    setForm((prev) => ({
      ...prev,
      [cameraField]: file,
    }));
  };

  // Determine if section is partially completed
  const determinePartialStatus = () => {
    // Check basic required fields
    if (!form.c1 || !form.c2 || !form.c3) {
      return false;
    }
    if (form.c2 === t.global.yes) {
      if (!form.c2Description?.trim()) {
        return false;
      }
      if (!form.c2Photo) {
        return false;
      }
      if (!form.c2Remarks?.trim()) {
        return false;
      }
    }

    if (form.c3 === t.global.yes) {
      if (!form.c3Date) {
        return false;
      }
    }

    return false;
  };

  // Validate form for submission
  const validateForm = () => {
    const newErrors = {};

    if (!form.c1?.trim()) {
      newErrors.c1 = t.global.err_a1;
    }

    if (!form.c2?.trim()) {
      newErrors.c2 = t.global.err_a1;
    }

    if (!form.c3?.trim()) {
      newErrors.c3 = t.global.err_a1;
    }

    if (form.c2 === t.global.yes) {
      if (!form.c2Description?.trim()) {
        newErrors.c2Description =
          "Description is required when YES is selected";
      }
    }

    if (form.c3 === t.global.yes) {
      if (!form.c3Date) {
        newErrors.c3Date = "Date of last test is required when YES is selected";
      }

      if (form.c3Date) {
        const selectedDate = new Date(form.c3Date);
        const today = new Date();
        if (selectedDate > today) {
          newErrors.c3Date = "Date cannot be in the future";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Convert YES/NO to numeric values
  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || 1);
      payload.append("ward_id", state?.ward_id || 5);
      payload.append("district_id", state?.district_id || 10);
      payload.append(
        "lattitude_longitude",
        state?.lattitude_longitude || "22.5726,88.3639",
      );
      payload.append("program_name", state?.area_name || "water");
      payload.append("current_section", "water");
      payload.append("kiosk_location_fulladdress", state?.fullAddress || "N/A");
      payload.append("reporting_date", new Date().toISOString().split("T")[0]);
      payload.append("area_id", state?.area_id || state?.id || 1);
      payload.append("canteen_type_id", state?.canteen_type || 2);

      payload.append("water_safety_C1", yesNoToInt(form.c1));
      payload.append("water_safety_C2", yesNoToInt(form.c2));
      payload.append("water_safety_C2_description", form.c2Description || "");
      payload.append("water_safety_C2_remarks", form.c2Remarks || "");

      if (form.c2Photo) {
        payload.append("water_safety_C2_upload", form.c2Photo);
      }

      payload.append("water_safety_C3", yesNoToInt(form.c3));
      payload.append("water_safety_C3_date", form.c3Date || "");
      payload.append("water_safety_C3_remarks", form.c3Remarks || "");

      if (form.c3Report) {
        payload.append("water_safety_C3_upload", form.c3Report);
      }

      // Determine partial status
      const isPartial = determinePartialStatus();

      // Save section data with partial flag
      const sectionDataWithPartial = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        submittedAsPartial: isPartial,
      };

      // Always save the data
      localStorage.setItem(
        "survey_section_c",
        JSON.stringify(sectionDataWithPartial),
      );

      // Update completed sections tracking
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      if (!isPartial) {
        completedSections.c = true;
      } else {
        delete completedSections.c;
      }
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Store partial sections separately
      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      if (isPartial) {
        partialSections.c = true;
      } else {
        delete partialSections.c;
      }
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      // Dispatch Redux action
      await dispatch(createCanteen(payload));

      // Navigate back to sections
      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error submitting Section C:", err);
      alert(t.global.alert_server_error);
      setIsSubmitting(false);
    }
  };

  const handleBackNavigation = () => {
    navigate("/survey/sections", { state });
  };

  const yesNoOptions = [t.global.yes, t.global.no];

  const getSurveyTypeDisplay = (type) => {
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return type || t.global.survey_type_default;
    }
  };

  // Calculate if form can be submitted
  const isFormSubmittable =
    form.c1 &&
    form.c2 &&
    form.c3 &&
    !(form.c2 === t.global.yes && !form.c2Description) &&
    !(form.c3 === t.global.yes && !form.c3Date);

  // Check if form will be submitted as partial
  const willBePartial = isFormSubmittable ? determinePartialStatus() : false;

  // Show completion status only when form is submittable
  const showCompletionStatus = isFormSubmittable;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.C.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.C.subtitle}
              </p>
              {saveStatus && (
                <div className="text-sm text-green-600 mt-1 flex items-center">
                  <CheckCircle size={14} className="mr-1" />
                  {saveStatus}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} {t.global.survey_type_default}
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* Partial Submission Warning - Only show when form is submittable */}
          {showCompletionStatus && willBePartial && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">
                    Partial Submission
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    This section will be saved as{" "}
                    <span className="font-semibold">partially completed</span>
                  </p>
                  <p className="text-amber-700 text-sm mt-2">
                    You can submit now and complete later, or add the missing
                    optional information for full completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* C1 - Safe Drinking Water Provision */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <Droplets size={18} className="mr-2" />
                C1. {t.modules.kiosk.sections.C.questions.provision}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c1 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c1"
                            value={option}
                            checked={form.c1 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c1 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c1 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c1}
                </p>
              )}
            </div>

            {/* C2 - Water Arrangement */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <FileUp size={18} className="mr-2" />
                C2. {t.modules.kiosk.sections.C.questions.arrangement}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c2 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c2"
                            value={option}
                            checked={form.c2 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c2 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c2 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c2}
                </p>
              )}

              {form.c2 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DESCRIPTION */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Description of arrangement:
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                      name="c2Description"
                      value={form.c2Description}
                      onChange={handleChange}
                      rows={3}
                      disabled={isSubmitting}
                      className={`w-full border p-3 rounded focus:ring-2 focus:ring-green-500 ${
                        errors.c2Description
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                    />
                    {errors.c2Description && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.c2Description}
                      </p>
                    )}
                  </div>

                  {/* PHOTO SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Photo Evidence ({t.global.optionalNote?.split(":")[0]}):
                    </label>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c2Photo");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c2Photo"
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    <input
                      type="file"
                      name="c2Photo"
                      id="c2Photo"
                      accept="image/*,.pdf"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        form.c2Photo
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c2Photo ? (
                        <div className="space-y-4">
                          {previewImages?.c2Photo && (
                            <img
                              src={previewImages.c2Photo}
                              alt="Preview"
                              className="max-h-48 mx-auto rounded-lg shadow"
                            />
                          )}

                          <div className="flex items-center justify-between">
                            <p className="text-sm break-all">
                              {form.c2Photo.name}
                            </p>

                            <button
                              type="button"
                              onClick={() => {
                                if (previewImages?.c2Photo) {
                                  URL.revokeObjectURL(previewImages.c2Photo);
                                }

                                setForm((prev) => ({ ...prev, c2Photo: null }));
                                setPreviewImages((prev) => ({
                                  ...prev,
                                  c2Photo: null,
                                }));
                              }}
                              className="text-red-600 text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint} photo
                        </p>
                      )}
                    </div>
                  </div>

                  {/* REMARKS */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      {t.global.remarks_label}
                    </label>
                    <textarea
                      name="c2Remarks"
                      value={form.c2Remarks}
                      onChange={handleChange}
                      rows={2}
                      disabled={isSubmitting}
                      className="w-full border border-gray-300 p-3 rounded"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* C3 - Water Quality Testing */}
            <div className="question-container border rounded-lg p-4">
              <label className="block font-medium mb-2 flex items-center">
                <TestTube size={18} className="mr-2" />
                C3. {t.modules.kiosk.sections.C.questions.testing}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                {yesNoOptions.map((option) => (
                  <label key={option} className="cursor-pointer">
                    <div
                      className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                        form.c3 === option
                          ? "bg-blue-50 border-blue-300 ring-2 ring-blue-100"
                          : "border-gray-200 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-center">
                        <div className="relative mr-2">
                          <input
                            type="radio"
                            name="c3"
                            value={option}
                            checked={form.c3 === option}
                            onChange={handleChange}
                            className="sr-only"
                            disabled={isSubmitting}
                            required
                          />
                          <div
                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              form.c3 === option
                                ? "border-blue-500 bg-blue-500"
                                : "border-gray-300"
                            }`}
                          >
                            {form.c3 === option && (
                              <div className="w-2 h-2 rounded-full bg-white"></div>
                            )}
                          </div>
                        </div>
                        <span className="font-medium text-gray-700">
                          {option}
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              {errors.c3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.c3}
                </p>
              )}

              {form.c3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  {/* DATE */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      Date of Inspection:
                      <span className="text-red-500 ml-1">*</span>
                    </label>
                    <input
                      type="date"
                      name="c3Date"
                      value={form.c3Date}
                      onChange={handleChange}
                      disabled={isSubmitting}
                      className={`w-full border p-3 rounded ${
                        errors.c3Date ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                  </div>

                  {/* PHOTO SECTION */}
                  <div>
                    <label className="block text-sm font-medium mb-3 text-gray-700">
                      Upload Test Report ({t.global.optionalNote?.split(":")[0]}):
                    </label>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <button
                        type="button"
                        onClick={() => {
                          setCameraField("c3Report");
                          setShowCamera(true);
                        }}
                        disabled={isSubmitting}
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Camera size={18} /> {t.global.use_camera}
                      </button>

                      <label
                        htmlFor="c3Report"
                        className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                      >
                        <Upload size={18} /> {t.global.upload_file}
                      </label>
                    </div>

                    <input
                      type="file"
                      name="c3Report"
                      id="c3Report"
                      accept="image/*,.pdf"
                      onChange={handleChange}
                      className="hidden"
                      disabled={isSubmitting}
                    />

                    {/* PREVIEW */}
                    <div
                      className={`border-2 border-dashed rounded-lg p-6 text-center ${
                        form.c3Report
                          ? "border-yellow-400 bg-yellow-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      {form.c3Report ? (
                        <div className="space-y-4">
                          {/* IMAGE PREVIEW ONLY IF IMAGE */}
                          {previewImages?.c3Report &&
                            form.c3Report.type.startsWith("image/") && (
                              <img
                                src={previewImages.c3Report}
                                alt="Preview"
                                className="max-h-48 mx-auto rounded-lg shadow"
                              />
                            )}

                          <div className="flex items-center justify-between">
                            <div className="text-left">
                              <p className="text-sm break-all">
                                {form.c3Report.name}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                Type: {form.c3Report.type}
                              </p>
                            </div>

                            <button
                              type="button"
                              onClick={() => {
                                if (previewImages?.c3Report) {
                                  URL.revokeObjectURL(previewImages.c3Report);
                                }

                                setForm((prev) => ({
                                  ...prev,
                                  c3Report: null,
                                }));
                                setPreviewImages((prev) => ({
                                  ...prev,
                                  c3Report: null,
                                }));
                              }}
                              className="text-red-600 text-xs font-bold"
                            >
                              {t.global.remove}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-gray-400 italic text-sm">
                          {t.global.capture_hint} test report
                        </p>
                      )}
                    </div>

                    {errors?.c3Report && (
                      <p className="mt-2 text-sm text-red-600">
                        {errors.c3Report}
                      </p>
                    )}
                  </div>

                  {/* REMARKS */}
                  <div>
                    <label className="block text-sm font-medium mb-1 text-gray-700">
                      {t.global.remarks_label}
                    </label>
                    <textarea
                      name="c3Remarks"
                      value={form.c3Remarks}
                      onChange={handleChange}
                      rows={2}
                      disabled={isSubmitting}
                      className="w-full border border-gray-300 p-3 rounded"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Completion Status - Only show when form is submittable */}
            {showCompletionStatus && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700">
                      Submission Status:
                    </span>
                  </div>
                  <div>
                    {willBePartial ? (
                      <span className="text-amber-600 font-medium">
                        Will be Partial
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">
                        Will be Complete
                      </span>
                    )}
                  </div>
                </div>

                {willBePartial && (
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Missing optional information will mark this section as
                      partially completed.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={handleBackNavigation}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <div className="flex flex-col items-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                    isSubmitting || !isFormSubmittable
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : willBePartial
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t.global.submitting}
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t.global.submit_section_c}
                    </>
                  )}
                </button>

                {isFormSubmittable && willBePartial && (
                  <p className="text-xs text-amber-600 mt-1 text-right">
                    Will be saved as partially completed
                  </p>
                )}
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  * Required fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-gray-600">{t.global.optionalNote?.split(":")[0]} fields</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            setPreviewImages((prev) => ({
              ...prev,
              [cameraField]: URL.createObjectURL(file),
            }));

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionCWaterSupply;